import { isPathMatches } from '@terminal/components/app-header/utils/navigation-utils';
import { getActiveFilterOptions } from '@terminal/hooks/use-filter-actions/use-filter-actions-utils';
import {
  applyDecompressedFilters,
  decompressFilters,
} from '@terminal/hooks/use-filters-query-param/utils';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { hasUsableScreenerFilters } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/utils';

import {
  clearActiveFilterOptions,
  getSavedScreenFilterGroups,
  mapFilters,
} from '../use-screener-store/filter-utils';
import {
  defaultTableState,
  useScreenerStore,
} from '../use-screener-store/useScreenerStore';
import type { InitialScreenerState } from './screenerInitialization.types';

const resolveSavedScreen = (
  screenerId: string,
  url: string
): InitialScreenerState | undefined => {
  const { defaultFilterGroups, savedScreens } = useScreenerStore.getState();
  const savedScreen = savedScreens.find(screen => screen.id === screenerId);

  if (!savedScreen) {
    return undefined;
  }

  const { filterGroups } = getSavedScreenFilterGroups(
    defaultFilterGroups,
    savedScreen.config
  );

  return {
    filterGroups,
    tableState: savedScreen.config.tableState ?? defaultTableState,
    savedScreen,
    url,
  };
};

export const resolveScreenerUrl = (
  url: string
): InitialScreenerState | undefined => {
  try {
    const parsedUrl = new URL(url, window.location.origin);

    if (!isPathMatches(['screener'], parsedUrl.pathname)) {
      return undefined;
    }

    const relativeUrl = `${parsedUrl.pathname}${parsedUrl.search}`;
    const screenerId = parsedUrl.pathname.match(
      new RegExp(`^${appPaths.screener}/([^/]+)`)
    )?.[1];

    if (screenerId) {
      return resolveSavedScreen(decodeURIComponent(screenerId), relativeUrl);
    }

    const compressedFilters = parsedUrl.searchParams.get(queryKeys.filters);

    if (!compressedFilters) {
      return undefined;
    }

    const { defaultFilterGroups } = useScreenerStore.getState();
    const decompressedFilters = decompressFilters(
      compressedFilters,
      defaultFilterGroups
    );

    if (!decompressedFilters) {
      return undefined;
    }

    const filterGroups = applyDecompressedFilters(
      clearActiveFilterOptions(defaultFilterGroups),
      decompressedFilters
    );
    const isScreenerExclusionEnabled = useFlags
      .getState()
      .getFeatureFlagState('PLATFORM-4767-screener-exclusion');
    const activeFilterOptions = getActiveFilterOptions(filterGroups);
    const hasPersistedExclusions = activeFilterOptions.some(
      filter => filter.selectionMode === 'exclude'
    );

    return hasUsableScreenerFilters(
      mapFilters(activeFilterOptions, isScreenerExclusionEnabled)
    ) ||
      (!isScreenerExclusionEnabled && hasPersistedExclusions)
      ? {
          filterGroups,
          tableState: defaultTableState,
          url: relativeUrl,
        }
      : undefined;
  } catch {
    return undefined;
  }
};
